import React, {useCallback, useEffect, useState, useMemo} from 'react';
import {Interest, InterestCategory, sanitizeString} from 'hubbl-shared';
import {PaddingBottom} from '../padding/PaddingBottom';
import {TextHeading7} from '../text/TextHeading7';
import {Input} from '../input/Input';
import {Api} from '../../api';
import {Palette} from 'hubbl-shared';
import {FontConfig} from '../../constants/FontConfig';
import {SecondaryButton} from '../buttons/secondary-button/SecondaryButton';
import {TextBodyMedium} from '../text/TextBodyMedium';
import {SelectablePill} from '../pill/SelectablePill';
import styled from 'styled-components';
import {useAppSelector, useI18n} from '../../hooks';
import {GlobalSelector} from '../../store/global/GlobalSelector';

// --- New SelectableInterestPill component ---
interface SelectableInterestPillProps {
  interest: Interest;
  isSelected: boolean;
  isDisabled?: boolean;
  onClick: (interest: Interest) => void;
}

const SelectableInterestPill: React.FC<SelectableInterestPillProps> = ({
  interest,
  isSelected,
  isDisabled = false,
  onClick,
}) => {
  const prefLanguage = useAppSelector(GlobalSelector.getPreferredLanguage);
  return (
    <button
      type="button"
      style={{
        border: 'none',
        outline: 'none',
        background: isSelected ? Palette.almostBlack : Palette.primaryCherokee,
        color: isSelected ? Palette.white : Palette.almostBlack,
        borderRadius: 20,
        padding: '8px 16px',
        margin: 2,
        fontWeight: FontConfig.Heading7.fontWeight,
        fontSize: FontConfig.Heading7.fontSize,
        lineHeight: FontConfig.Heading7.lineHeight,
        fontFamily: FontConfig.Heading7.fontFamily,
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        opacity: isDisabled ? 0.5 : 1,
        transition: 'background 0.2s, color 0.2s',
      }}
      disabled={isDisabled}
      onClick={() => !isDisabled && onClick(interest)}>
      {interest.label[prefLanguage]} {interest.emoji ? interest.emoji : ''}
    </button>
  );
};
// --- End SelectableInterestPill ---

// Styled components for collapsible categories
const CategoryContainer = styled.div`
  margin-bottom: 16px;
  border: 1px solid ${Palette.almostBlackTransparent5};
  border-radius: 12px;
  overflow: hidden;
  background: ${Palette.white};
`;

const CategoryHeader = styled.button<{isExpanded: boolean}>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: ${Palette.white};
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;

  &:hover {
    background: ${Palette.primaryBianca}30;
  }
`;

const CategoryTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SelectedCount = styled.span<{hasSelected: boolean}>`
  background: ${props =>
    props.hasSelected ? Palette.almostBlack : Palette.almostBlackTransparent5};
  color: ${props =>
    props.hasSelected ? Palette.white : Palette.almostBlack500};
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 11px;
`;

const ChevronIcon = styled.div<{isExpanded: boolean}>`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;
  transform: ${props => (props.isExpanded ? 'rotate(180deg)' : 'rotate(0deg)')};
  color: ${Palette.almostBlack500};
  font-size: 14px;
`;

const CategoryContent = styled.div<{isExpanded: boolean}>`
  max-height: ${props => (props.isExpanded ? '500px' : '0')};
  overflow: hidden;
  transition: max-height 0.3s ease;
`;

const InterestsGrid = styled.div`
  padding: 16px 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const SelectedInterestsContainer = styled.div`
  margin-bottom: 16px;
`;

const SelectedPillsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const SelectedPill = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  background: ${Palette.almostBlack};
  color: ${Palette.white};
  border-radius: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  gap: 8px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${Palette.almostBlack}dd;
    transform: translateY(-1px);
  }
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: ${Palette.white};
  padding: 0;
  margin: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  opacity: 0.8;
  transition: opacity 0.2s ease;
  margin-left: 4px;

  &:hover {
    opacity: 1;
    scale: 1.1;
  }
`;

interface InterestsSelectorProps {
  selectedInterests: Interest[];
  onInterestsChange: (selected: Interest[]) => void;
  maxInterestsSelectable?: number;
}

export const InterestsSelector: React.FC<InterestsSelectorProps> = ({
  selectedInterests,
  onInterestsChange,
  maxInterestsSelectable = 10,
}) => {
  const [categories, setCategories] = useState<InterestCategory[]>([]);
  const t = useI18n();
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(),
  );

  const preferredLanguage = useAppSelector(GlobalSelector.getPreferredLanguage);

  useEffect(() => {
    const fetchCategories = async () => {
      setLoading(true);
      try {
        const cats = await Api.interests.getInterestCategories();
        setCategories(cats);
      } catch (e) {
        setCategories([]);
      }
      setLoading(false);
    };
    fetchCategories();
  }, []);

  const onToggleInterest = useCallback(
    (interest: Interest) => {
      const exists = selectedInterests.some(i => i.id === interest.id);
      let updated: Interest[];
      if (exists) {
        updated = selectedInterests.filter(i => i.id !== interest.id);
      } else {
        if (selectedInterests.length < maxInterestsSelectable) {
          updated = [...selectedInterests, interest];
        } else {
          updated = selectedInterests;
        }
      }
      onInterestsChange(updated);
    },
    [selectedInterests, onInterestsChange, maxInterestsSelectable],
  );

  const onRequestNewInterest = useCallback(() => {
    const subject = encodeURIComponent(t.interests.newInterestRequestSubject);
    const body = encodeURIComponent(t.interests.newInterestRequestBody);
    window.open(`mailto:emil@hubbl.co?subject=${subject}&body=${body}`);
  }, [t]);

  // Filter categories and interests based on search query - exactly like mobile
  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) {
      return categories;
    }

    const sanitizedSearchText = sanitizeString(searchQuery);
    return categories
      .map(category => ({
        ...category,
        interests: category.interests.filter(interest => {
          const interestLabel = interest.label?.[preferredLanguage] || '';
          const sanitizedInterestLabel = sanitizeString(interestLabel);
          return sanitizedInterestLabel.includes(sanitizedSearchText);
        }),
      }))
      .filter(category => category.interests.length > 0);
  }, [categories, searchQuery, preferredLanguage]);

  // Auto-expand/collapse categories based on search
  useEffect(() => {
    if (searchQuery.trim()) {
      // When searching, expand all categories that have matching interests
      const categoriesWithMatches = filteredCategories.map(
        category => category.id,
      );
      setExpandedCategories(new Set(categoriesWithMatches));
    } else {
      // When search is cleared, collapse all categories
      setExpandedCategories(new Set());
    }
  }, [searchQuery, filteredCategories]);

  const toggleCategory = useCallback((categoryId: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  }, []);

  const removeSelectedInterest = useCallback(
    (interestToRemove: Interest) => {
      onInterestsChange(
        selectedInterests.filter(
          interest => interest.id !== interestToRemove.id,
        ),
      );
    },
    [selectedInterests, onInterestsChange],
  );

  const getSelectedCountForCategory = useCallback(
    (category: InterestCategory) => {
      return category.interests.filter(interest =>
        selectedInterests.some(selected => selected.id === interest.id),
      ).length;
    },
    [selectedInterests],
  );

  if (loading) return <div>{t.common.loadingInterests}</div>;
  if (!categories.length) return <div>{t.common.noInterestsFound}</div>;

  return (
    <div>
      <Input
        placeholder={t.common.searchInterests}
        value={searchQuery}
        onChange={e => setSearchQuery(e.target.value)}
        isCompact
      />
      <PaddingBottom amount={16} />

      {selectedInterests.length > 0 && (
        <SelectedInterestsContainer>
          <TextHeading7
            text={`${t.common.selected} (${selectedInterests.length})`}
            color={Palette.almostBlack500}
          />
          <PaddingBottom amount={8} />
          <SelectedPillsContainer>
            {selectedInterests.map(interest => (
              <SelectedPill
                key={interest.id}
                onClick={() => removeSelectedInterest(interest)}>
                {interest.label[preferredLanguage]}
                {interest.emoji && ` ${interest.emoji}`}
                <RemoveButton
                  onClick={e => {
                    e.stopPropagation();
                    removeSelectedInterest(interest);
                  }}>
                  ✕
                </RemoveButton>
              </SelectedPill>
            ))}
          </SelectedPillsContainer>
          <PaddingBottom amount={16} />
        </SelectedInterestsContainer>
      )}

      {filteredCategories.map(category => {
        const isExpanded = expandedCategories.has(category.id);
        const selectedCount = getSelectedCountForCategory(category);
        const hasSelected = selectedCount > 0;

        return (
          <CategoryContainer key={category.id}>
            <CategoryHeader
              isExpanded={isExpanded}
              onClick={() => toggleCategory(category.id)}>
              <CategoryTitle>
                <TextHeading7 text={category.label[preferredLanguage]} />

                <SelectedCount hasSelected={hasSelected}>
                  {selectedCount}/{category.interests.length}
                </SelectedCount>
              </CategoryTitle>
              <ChevronIcon isExpanded={isExpanded}>▼</ChevronIcon>
            </CategoryHeader>

            <CategoryContent isExpanded={isExpanded}>
              <InterestsGrid>
                {category.interests.map(interest => (
                  <SelectableInterestPill
                    key={category.id + interest.id}
                    interest={interest}
                    isSelected={selectedInterests.some(
                      i => i.id === interest.id,
                    )}
                    isDisabled={
                      !selectedInterests.some(i => i.id === interest.id) &&
                      selectedInterests.length >= maxInterestsSelectable
                    }
                    onClick={onToggleInterest}
                  />
                ))}
              </InterestsGrid>
            </CategoryContent>
          </CategoryContainer>
        );
      })}

      <div style={{textAlign: 'center', marginTop: '32px'}}>
        <TextBodyMedium
          text={t.common.cantFindWhatYoureLookingFor}
          color={Palette.almostBlack500}
        />
        <PaddingBottom amount={8} />
        <SecondaryButton
          isCompact
          customWidth={200}
          label={t.interests.requestNewInterest}
          onClick={onRequestNewInterest}
        />
      </div>
    </div>
  );
};
